/**
 * Shared tracking utilities for conversion tracking (Google Ads, GTM, Meta Pixel).
 *
 * These functions are called after booking/purchase completion events so that
 * partners can track conversions without writing any custom JavaScript.
 */

type TrackingData = {
  listingId?: string;
  businessId?: string;
  purchaseId?: string;
  total?: number;
  currency?: string;
  email?: string;
  courseName?: string;
  grossTotal?: number;
  netTotal?: number;
  discountAmount?: number;
  discountType?: string;
};

async function hashEmail(email: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(email.trim().toLowerCase());
  const hashBuffer = await crypto.subtle.digest("SHA-256", data);
  return Array.from(new Uint8Array(hashBuffer))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

/**
 * Push a booking/purchase event to GTM dataLayer with Enhanced Conversions data.
 * Partners using GTM can set up a trigger on the event name without any custom JS.
 */
export async function pushToDataLayer(
  eventName: string,
  data: TrackingData,
): Promise<void> {
  if (typeof window === "undefined") return;

  const w = window as any;
  w.dataLayer = w.dataLayer || [];

  const grossValue = data.grossTotal != null ? data.grossTotal / 100 : (data.total != null ? data.total / 100 : 0);
  const netValue = data.netTotal != null ? data.netTotal / 100 : (data.total != null ? data.total / 100 : 0);

  const payload: Record<string, unknown> = {
    event: eventName,
    baluu_listing_id: data.listingId,
    baluu_business_id: data.businessId,
    baluu_purchase_id: data.purchaseId,
    baluu_value: grossValue,
    baluu_net_value: netValue,
    baluu_discount_value: data.discountAmount != null ? data.discountAmount / 100 : 0,
    baluu_discount_type: data.discountType || "",
    baluu_currency: data.currency || "GBP",
  };

  if (data.email) {
    try {
      payload.baluu_email_sha256 = await hashEmail(data.email);
    } catch {
      // crypto.subtle may not be available in insecure contexts
    }
  }

  w.dataLayer.push(payload);
}

/**
 * Push a standard GA4 Enhanced Ecommerce `purchase` event to the dataLayer.
 *
 * This is in ADDITION to the custom `baluu_*` events — it lets schools use GA4's
 * native ecommerce reports and Google Ads' default "import a GA4 purchase
 * conversion" flow without manually mapping our custom variables. The
 * `transaction_id` lets GA4 dedupe so a school mapping both this and a custom
 * event to the same purchase is not double-counted.
 */
export async function pushToGA4Ecommerce(data: TrackingData): Promise<void> {
  if (typeof window === "undefined") return;

  const w = window as any;
  w.dataLayer = w.dataLayer || [];

  const value = data.grossTotal != null ? data.grossTotal / 100 : (data.total != null ? data.total / 100 : 0);

  // GA4 recommends clearing the previous ecommerce object so values from an
  // earlier event don't bleed into this one.
  w.dataLayer.push({ ecommerce: null });

  const payload: Record<string, unknown> = {
    event: "purchase",
    ecommerce: {
      transaction_id: data.purchaseId,
      value,
      currency: data.currency || "GBP",
      items: [
        {
          item_id: data.listingId || data.purchaseId,
          item_name: data.courseName || "Booking",
          price: value,
          quantity: 1,
        },
      ],
    },
  };

  if (data.email) {
    try {
      payload.baluu_email_sha256 = await hashEmail(data.email);
    } catch {
      // crypto.subtle may not be available in insecure contexts
    }
  }

  w.dataLayer.push(payload);
}

/**
 * Fire a Meta Pixel Purchase event if fbq is present on the page.
 * Partners only need the Meta Pixel base code -- this handles the purchase event.
 */
export function pushToMetaPixel(data: TrackingData): void {
  if (typeof window === "undefined") return;

  const fbq = (window as any).fbq;
  if (typeof fbq !== "function") return;

  const value = data.grossTotal != null ? data.grossTotal / 100 : (data.total != null ? data.total / 100 : 0);

  fbq("track", "Purchase", {
    value,
    currency: data.currency || "GBP",
    content_ids: [data.listingId].filter(Boolean),
    content_type: "product",
  });
}

/**
 * Fire a Google Ads conversion event if gtag is present on the page
 * and a conversion ID + label are provided.
 */
export function pushToGoogleAds(
  conversionId: string,
  conversionLabel: string,
  data: TrackingData,
): void {
  if (typeof window === "undefined") return;

  const gtag = (window as any).gtag;
  if (typeof gtag !== "function") return;

  const value = data.grossTotal != null ? data.grossTotal / 100 : (data.total != null ? data.total / 100 : 0);

  gtag("event", "conversion", {
    send_to: `${conversionId}/${conversionLabel}`,
    value,
    currency: data.currency || "GBP",
    transaction_id: data.purchaseId,
  });
}

/**
 * Fire all tracking events for a completed booking or purchase.
 * Centralises dataLayer, Meta Pixel, and optional Google Ads conversion.
 *
 * `extraDataLayerEvents` pushes additional event names to the dataLayer with the
 * same payload but WITHOUT re-firing the Meta Pixel / Google Ads conversion, so
 * we can emit a secondary event name (e.g. `baluu_basket_checkout_completed`)
 * for forward compatibility without double-counting conversions.
 */
export async function trackConversion(
  eventName: string,
  data: TrackingData,
  googleAds?: { conversionId: string; conversionLabel: string },
  extraDataLayerEvents?: string[],
): Promise<void> {
  await pushToDataLayer(eventName, data);
  // Standard GA4 ecommerce `purchase` event (in addition to the custom events).
  await pushToGA4Ecommerce(data);
  pushToMetaPixel(data);

  if (googleAds?.conversionId && googleAds?.conversionLabel) {
    pushToGoogleAds(googleAds.conversionId, googleAds.conversionLabel, data);
  }

  if (extraDataLayerEvents?.length) {
    for (const extraEvent of extraDataLayerEvents) {
      if (extraEvent && extraEvent !== eventName) {
        await pushToDataLayer(extraEvent, data);
      }
    }
  }
}
